import { Box, Button, FormControl, FormHelperText, InputLabel, TextField } from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import GoBack from "../shared/GoBack";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { DateField, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  ChangeSolvingServiceReport,
  ServiceReport,
  ServiceReportFile,
  ServiceReportFileTypeCode
} from "../../models/serviceReportsModels";
import { CurrencyCode } from "../../../common/models/CommonModels";
import { useNavigate } from "react-router-dom";
import { PublicState, createSolvingServiceReport, getMachineParts, getServiceReports } from "../../publicSlice";
import ReportFile from "../../../shared/ReportFile";
import colors from "../../../shared/themes/colors";
import { dt } from "../../../shared/themes/redesignTokens";
import ChevronDownIcon from "../../../shared/assets/icons/ChevronDownIcon";
import Moment from "react-moment";
import { MAX_FILE_SIZE_MB } from "../../../shared/models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import { localizeDateFormat } from "../../v2/shared/dateFormat";
import usePermission from "../../../shared/hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import KioskBackButton from "../kiosk/KioskBackButton";

const SolvingServiceReport = () => {
  const { machine, serviceReport, machineParts } = useAppSelector<PublicState>((state: any) => state.public);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate, i18n } = useTranslation(["public", "servicing"]);
  const { translate: tCommon } = useTranslation("common");
  const dateTimeFormat = localizeDateFormat("DD.MM.YYYY, HH:mm", i18n.language);
  // SEC-REQ-3 (KV2-0): solving a report (mark solved + record cost) requires Write.
  // Defense-in-depth guard so a non-Write user (e.g. Viewer) who reaches this form
  // directly is redirected out instead of submitting to a 403.
  const { canWrite } = usePermission(Modules.Machinery);

  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ChangeSolvingServiceReport>();

  const closeDate = watch("closeDate");
  const serviceReportNumber = serviceReport?.number || undefined;
  const [openServiceReportInfo, setOpenServiceReportInfo] = useState(false);
  const partName = (() => {
    if (!serviceReport?.affectedPartCategoryId) return undefined;
    if (machineParts && machineParts.length > 0) {
      for (const group of machineParts) {
        const found = group.parts.find((p) => p.id === serviceReport.affectedPartCategoryId);
        if (found) {
          const translationKey = found.code.replace(/_([a-z])/g, (_m: string, c: string) => c.toUpperCase());
          return translate(`servicing:machineParts.parts.${translationKey}`);
        }
      }
    }
    return undefined;
  })();

  const handleOpenServiceReportInfo = () => {
    setOpenServiceReportInfo(!openServiceReportInfo);
  };

  const goToMachine = () => {
    if (machine?.internalNumber) {
      navigate(`/public/machines/${machine.internalNumber}`);
    }
  };

  const [newFiles, setFiles] = useState<ServiceReportFile[]>([]);
  const [error, setError] = useState<string>();

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];

    selectedFiles.forEach((file, i) => {
      processFile(file);
    });

    event.target.value = "";
  };

  const processFile = async (file: File) => {
    setError(undefined);

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(translate("public:fileTooBig"));
      return;
    }

    const url = await detectUrl(file);

    if (url) {
      const newFile: ServiceReportFile = {
        name: file.name,
        content: url,
        typeCode: ServiceReportFileTypeCode.solvingReportDocument,
        size: file.size
      };

      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, newFile];
        return updatedFiles;
      });
    }
  };

  const detectUrl = (file: Blob | null) => {
    return new Promise<string | null>((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDelete = (fileIndex: number) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== fileIndex);
      return updatedFiles;
    });
  };

  const onSubmit: SubmitHandler<ChangeSolvingServiceReport> = (data: ChangeSolvingServiceReport) => {
    handleCreateServiceReport(data);
  };

  const handleCreateServiceReport = async (model: ChangeSolvingServiceReport) => {
    model.closeDate = model.closeDate || moment().toDate();
    model.files = newFiles;
    model.cost = { id: CurrencyCode.usd, value: Number(model.cost?.value || 0) };
    if (serviceReportNumber && machine?.internalNumber) {
      await dispatch(createSolvingServiceReport({ model, serviceReportNumber }));
      await dispatch(getServiceReports({ machineNumber: machine.internalNumber, query: { statusCodes: ["InProgress", "Solved"] } }));
      navigate(`/public/service-reports/${serviceReportNumber}`);
    }
  };

  useEffect(() => {
    dispatch(getMachineParts());
    if (!serviceReport) {
      reset({} as ServiceReport);
    }
  }, [serviceReport?.number, reset, dispatch]);

  useEffect(() => {
    if (!canWrite) {
      if (machine?.internalNumber) {
        navigate(`/public/machines/${machine.internalNumber}`, { replace: true });
      } else {
        navigate("/public", { replace: true });
      }
    }
  }, [canWrite, machine?.internalNumber, navigate]);

  if (!canWrite) {
    return null;
  }

  return (
    <Box className="h-full" sx={{ px: 2, pb: "80px" }}>
      <GoBack title={translate("markServiceReportAsSolved")} />

      <Box className="mt-4" sx={{ height: "calc(100% - 100px)" }}>
        <form id="form" className="h-full" onSubmit={handleSubmit(onSubmit)}>
          <Box className="flex flex-col justify-between h-full">
            <Box className="flex flex-col gap-6">
              <Box
                className="p-4 rounded-lg"
                sx={{ background: colors.secondary.gray100, border: `1px solid ${colors.secondary.gray80}` }}
              >
                <div onClick={handleOpenServiceReportInfo}>
                  <Box>
                    <Box className="flex items-center justify-between text-18-medium" color={dt.blue}>
                      <Box>{translate("serviceReportDetails")}</Box>
                      <Box sx={{ rotate: openServiceReportInfo ? "180deg" : "" }}>
                        <ChevronDownIcon />
                      </Box>
                    </Box>

                    {openServiceReportInfo && (
                      <Box>
                        <Box className="flex flex-col gap-4 mt-4">
                          <Box className="text-16-bold" color="#333333">
                            {serviceReport?.title}
                          </Box>

                          <Box>
                            <Box className="text-14-medium" color={colors.secondary.gray70}>
                              {translate("componentAffected")}
                            </Box>
                            <Box className="text-16-medium" color="#333333">
                              {partName}
                            </Box>
                          </Box>

                          <Box>
                            <Box className="text-14-medium" color={colors.secondary.gray40}>
                              {translate("reportDescription")}
                            </Box>
                            <Box className="text-16-regular" color="#333333">
                              {serviceReport?.description}
                            </Box>
                          </Box>

                          <Box className="grid grid-cols-2 gap-6">
                            <Box className="md:flex md:items-center md:gap-2">
                              <Box className="text-14-medium" color={colors.secondary.gray70}>
                                {translate("reportNo")}
                              </Box>
                              <Box className="text-16-medium" color="#333333">
                                {serviceReport?.number}
                              </Box>
                            </Box>

                            <Box className="md:flex md:items-center md:gap-2">
                              <Box className="text-14-medium" color={colors.secondary.gray70}>
                                {translate("openDate")}
                              </Box>
                              <Box className="text-16-medium" color="#333333">
                                <Moment date={serviceReport?.openDate} format={dateTimeFormat} />
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </div>
              </Box>

              <Box>
                <InputLabel>{translate("closeDate")}</InputLabel>

                <FormControl className="w-full">
                  <Controller
                    control={control}
                    name="closeDate"
                    rules={{
                      validate: (value) => {
                        if (value && serviceReport?.openDate) {
                          if (moment(value) > moment()) {
                            return translate("kioskV2CloseDateFuture");
                          }
                          return (
                            moment(value) >= moment(serviceReport.openDate) ||
                            translate("kioskV2CloseDateBeforeOpen", { date: moment(serviceReport.openDate).format(dateTimeFormat) })
                          );
                        }
                      }
                    }}
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          {...field}
                          value={moment(field.value)}
                          onChange={(value) => field.onChange(value?.toDate())}
                          format={localizeDateFormat("DD.MM.YYYY", i18n.language)}
                          minDate={moment(serviceReport?.openDate)}
                          disableFuture
                        />
                      </LocalizationProvider>
                    )}
                  />
                </FormControl>
              </Box>

              <Box>
                <InputLabel>{translate("time")}</InputLabel>
                <FormControl className="w-full">
                  <Controller
                    control={control}
                    name="closeDate"
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateField
                          {...field}
                          value={moment(closeDate)}
                          onChange={(value) => field.onChange(value?.toDate())}
                          format="HH:mm"
                        />
                      </LocalizationProvider>
                    )}
                  />
                  <FormHelperText error>{errors.closeDate?.message}</FormHelperText>
                </FormControl>
              </Box>

              <Box>
                <InputLabel>{translate("closingReport")}</InputLabel>
                <TextField
                  className="w-full textarea"
                  multiline
                  rows={6}
                  {...register("solvingDescription")}
                  error={!!errors.solvingDescription}
                  helperText={errors.solvingDescription?.message}
                  inputProps={{ maxLength: 2000 }}
                />
              </Box>

              <Box>
                <InputLabel>{translate("costEuro")}</InputLabel>
                <TextField
                  type="number"
                  className="w-full"
                  variant="outlined"
                  {...register("cost.value", {
                    required: tCommon("forms.required"),
                    max: { value: 100000, message: translate("maxValue") },
                    min: { value: 0, message: translate("minValue") }
                  })}
                  error={!!errors.cost?.value}
                  helperText={errors.cost?.value?.message}
                />
                <input type="hidden" {...register("cost.id", { value: CurrencyCode.eur })} />
              </Box>

              <Box>
                <label htmlFor="file-input">
                  <Button component="span" variant="outlined" className="w-full">
                    {translate("uploadImage")}
                  </Button>
                  {!!error && (
                    <FormHelperText
                      error
                      sx={{
                        padding: "6px 8px",
                        margin: 0,
                        textAlign: "center",
                        minHeight: "40px",
                        maxWidth: "370px"
                      }}
                    >
                      {error === "File is too big" ? translate("fileTooBig") : error}
                    </FormHelperText>
                  )}
                </label>

                <Box>
                  <TextField
                    type="file"
                    id="file-input"
                    sx={{ display: "none" }}
                    inputProps={{ multiple: true }}
                    onChange={handleFileChange}
                  />

                  {newFiles.length > 0 && (
                    <Box className="flex flex-col gap-2" sx={{ mt: 1 }}>
                      {newFiles.map((file, i) => (
                        <ReportFile
                          key={i}
                          file={file}
                          onDelete={() => handleDelete(i)}
                          canDownload={true}
                          fileIndex={i}
                          isNew={true}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box className="flex gap-4 pt-4">
              <Button variant="outlined" className="w-full" onClick={goToMachine}>
                {tCommon("buttons.cancel")}
              </Button>

              <Button form="form" type="submit" className="w-full" variant="contained">
                {translate("saveAsSolved")}
              </Button>
            </Box>
          </Box>
        </form>
      </Box>

      <KioskBackButton />
    </Box>
  );
};

export default SolvingServiceReport;